import { Dropdown } from "@nova/component-dropdown"
import { ColorSwatchCategory, hexColorSwatches, IconPackOption, IconShape } from "@nova/constants"
import {
    iconByShape,
    iconsByIconPack,
    shapeIcons,
} from "features/map/inspector-panel/icons/constants"
import { FunctionComponent, useMemo } from "react"
import * as Icons from "@nova/component-icons"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { ColorPalette } from "features/map/inspector-panel/icons/TODO-put-in-package/ColorPalette"
import { InspectorPanelIconPackIcon } from "features/map/inspector-panel/icons/icon-pack-dropdown/InspectorPanelIconPackIcon"
import { formatIconUrl } from "features/map/inspector-panel/icons/module"
import { ScrollableArea } from "@nova/component-scrollable-area"

interface IconPackDropdownProps {
    value: IconPackOption
    onChange: (value: IconPackOption) => void
}

const IconPackDropdown: FunctionComponent<IconPackDropdownProps> = ({ onChange, value }) => {
    return (
        <div className="flex w-full gap-2.5 items-center">
            <h5 className="text-white basis-24 shrink-0">Icon Pack</h5>

            <Dropdown>
                <Dropdown.TriggerButton className="truncate">
                    <small className="truncate text-ellipsis">{value}</small>
                </Dropdown.TriggerButton>
                <Dropdown.Menu className="flex flex-col max-w-[150px]" position="bottom-left">
                    <Dropdown.ButtonItem key="Shape" onClick={() => onChange(IconPackOption.Shape)}>
                        Shape
                    </Dropdown.ButtonItem>
                    {Object.keys(iconsByIconPack).map((pack) => (
                        <Dropdown.ButtonItem
                            key={pack}
                            onClick={() => onChange(pack as keyof typeof iconsByIconPack)}
                        >
                            {pack}
                        </Dropdown.ButtonItem>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

interface IconShapeDropdownProps {
    value: IconShape
    onChange: (value: IconShape) => void
    color: number
}

const IconShapeDropdown: FunctionComponent<IconShapeDropdownProps> = ({
    onChange,
    value,
    color,
}) => {
    return (
        <div className="flex w-full gap-2.5 items-center">
            <h5 className="text-white w-24">Shape</h5>

            <Dropdown>
                <Dropdown.TriggerButton>
                    <Icons.SmallIcon
                        Icon={iconByShape[value]}
                        style={{ color: decodeNumberToColor(color) }}
                        className="hover:brightness-125 cursor-pointer"
                        accentColor={decodeNumberToColor(color)}
                    />
                </Dropdown.TriggerButton>
                <Dropdown.Menu position="bottom-left" className="flex gap-2">
                    {shapeIcons.map(([shapeOption, Icon], i) => (
                        <Dropdown.Item
                            key={i}
                            onClick={() => {
                                onChange(shapeOption)
                            }}
                        >
                            <Icons.SmallIcon
                                Icon={Icon}
                                style={{ color: decodeNumberToColor(color) }}
                                className="hover:brightness-125 cursor-pointer"
                                accentColor={decodeNumberToColor(color)}
                            />
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

interface IconColorDropdownProps {
    value: number
    onChange: (value: number) => void
}

const iconColorSwatches = hexColorSwatches[ColorSwatchCategory.Icon].map((hex) =>
    encodeColorToNumber(hex)
)

const IconColorDropdown: FunctionComponent<IconColorDropdownProps> = ({ onChange, value }) => {
    return (
        <div className="flex w-full gap-2.5 items-center">
            <h5 className="text-white w-24">Color</h5>
            <Dropdown>
                <Dropdown.TriggerButton>
                    <Icons.SmallIcon
                        Icon={Icons.BucketColor}
                        className="hover:brightness-125 cursor-pointer"
                        accentColor={decodeNumberToColor(value)}
                    />
                </Dropdown.TriggerButton>
                <Dropdown.Menu unstyled position="bottom-left" className="flex gap-2">
                    <ColorPalette
                        swatches={iconColorSwatches}
                        selectedColor={value}
                        setSelectedColor={onChange}
                        setOpenDropdownId={() => {}}
                    />
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

interface IconDropdownProps {
    iconPack: Exclude<IconPackOption, "Shape">
    value: string
    onChange: (value: string) => void
}

const IconDropdown: FunctionComponent<IconDropdownProps> = ({ onChange, value, iconPack }) => {
    const filteredIconPack = useMemo(() => {
        return Object.entries(iconsByIconPack[iconPack])
    }, [iconPack])
    return (
        <div className="flex w-full gap-2.5 items-center">
            <h5 className="text-white w-24">Icon</h5>

            <Dropdown>
                <Dropdown.TriggerButton>
                    <svg
                        data-src={formatIconUrl(value)}
                        className="hover:brightness-125 cursor-pointer"
                        height={20}
                        width={20}
                    />
                </Dropdown.TriggerButton>
                <Dropdown.Menu position="bottom-left">
                    <ScrollableArea className="flex flex-col max-h-80 gap-3">
                        {filteredIconPack.map(([sectionTitle, icons], i) => (
                            <div key={i} className="flex flex-col gap-2">
                                {/* Icon Section Title */}
                                <h5 className="text-gray-0 select-none">{sectionTitle}</h5>

                                {/* Icon Grid for This Section */}
                                <div className="grid grid-cols-[repeat(6,32px)] justify-between gap-y-5">
                                    {Object.entries(icons).map(([id, { name, url }], j) => (
                                        <InspectorPanelIconPackIcon
                                            key={j}
                                            id={id}
                                            name={name}
                                            url={url}
                                            index={j}
                                            isSelected={url === value}
                                            onSelect={() => {
                                                onChange(url)
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </ScrollableArea>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    )
}

export { IconPackDropdown, IconShapeDropdown, IconColorDropdown, IconDropdown }
